import {
    AiCallsAgentType,
    AiCallsAmbientSound,
} from '~graphql/generated/graphql'

const INSTRUCTION_DELIMITER = '\n'

/** Split a persisted newline-delimited string into individual instruction items. */
export function parseInstructionsString(
    raw: string | null | undefined,
): string[] {
    if (!raw) return []
    return raw
        .split(INSTRUCTION_DELIMITER)
        .filter(line => line.trim().length > 0)
}

/** Join instruction items back into a single newline-delimited string for persistence. */
export function serializeInstructions(items: string[]): string {
    return items.filter(line => line.trim().length > 0).join(INSTRUCTION_DELIMITER)
}

// ----------------------------------------------------------------------------
// Voice tuning presets + platform defaults
//
// We expose voice speed and interruption sensitivity as named presets rather
// than raw float inputs, because no clinic admin reasons in terms of
// "0.8 interruption sensitivity". Presets map to Retell's underlying scale
// (0-1 for sensitivity, 0.5-2.0 for voice speed). Admins can still pass any
// number from the API.
//
// The `PLATFORM_DEFAULT_*` constants mirror the ones in
// `server/src/utils/retell/retell.service.ts`. They live here so the
// "Platform default" UI label can show the user what they're inheriting
// without making the dashboard reach across the network for a value the
// server already knows. KEEP THESE IN SYNC. If we ever bump the server-side
// default (e.g. swap office-ambience for something else), update this file
// in the same PR. The retell.service.ts defaults remain the source of truth
// for what's actually pushed to the Retell agent at create time.
// ----------------------------------------------------------------------------

/** Sentinel for "use the platform default" in select inputs. */
export const PLATFORM_DEFAULT_PRESET = 'PLATFORM_DEFAULT'

export const PLATFORM_DEFAULT_AMBIENT_SOUND: AiCallsAmbientSound =
    AiCallsAmbientSound.Off
export const PLATFORM_DEFAULT_VOICE_SPEED = 0.96
export const PLATFORM_DEFAULT_INTERRUPTION_SENSITIVITY = 0.8
export const PLATFORM_DEFAULT_ENABLE_DYNAMIC_VOICE_SPEED = false

interface NumericPreset {
    /** Stable identifier used as the select's value key. */
    id: string
    /** Human-readable label shown in the dropdown. */
    label: string
    /** Underlying float value pushed to Retell. */
    value: number
}

/**
 * Voice speed presets in our supported range. The float values were tuned
 * against the inbound flow's `voice_speed: 0.96` baseline — the order goes
 * slower-than-default → default → faster-than-default.
 */
export const VOICE_SPEED_PRESETS: readonly NumericPreset[] = [
    { id: 'slower', label: 'Slower', value: 0.85 },
    { id: 'normal', label: 'Normal', value: 0.96 },
    { id: 'faster', label: 'Faster', value: 1.1 },
] as const

/**
 * Interruption sensitivity presets. Lower values = agent is more patient
 * and lets the caller finish thoughts before responding. Our current
 * platform default is 0.8.
 */
export const INTERRUPTION_SENSITIVITY_PRESETS: readonly NumericPreset[] = [
    { id: 'patient', label: 'Patient', value: 0.5 },
    { id: 'balanced', label: 'Balanced', value: 0.8 },
    { id: 'quick', label: 'Quick', value: 1.0 },
] as const

/**
 * Resolve a stored float value back to a preset id (for select default
 * value). Returns {@link PLATFORM_DEFAULT_PRESET} when the value is null,
 * and the closest preset by exact match otherwise (presets are sparse, so
 * a non-matching value indicates an admin-side custom override — we surface
 * that as the "custom value" option upstream).
 */
export function resolvePresetId(
    presets: readonly NumericPreset[],
    value: number | null,
): string {
    if (value === null) return PLATFORM_DEFAULT_PRESET
    const exact = presets.find(p => p.value === value)
    return exact ? exact.id : 'custom'
}

/** Look up a preset's float value by id, returning `null` for the sentinel. */
export function resolvePresetValue(
    presets: readonly NumericPreset[],
    id: string,
): number | null {
    if (id === PLATFORM_DEFAULT_PRESET) return null
    const preset = presets.find(p => p.id === id)
    return preset ? preset.value : null
}

/**
 * Display labels for the AI ambient sound enum. Keep aligned with the
 * server's `AiCallsAmbientSound` enum — order is the dropdown order.
 */
export const AMBIENT_SOUND_LABELS: Record<AiCallsAmbientSound, string> = {
    [AiCallsAmbientSound.Off]: 'Off (no background noise)',
    [AiCallsAmbientSound.CallCenter]: 'Call center (busy)',
    [AiCallsAmbientSound.CoffeeShop]: 'Coffee shop',
    [AiCallsAmbientSound.ConventionHall]: 'Convention hall',
    [AiCallsAmbientSound.SummerOutdoor]: 'Summer outdoor',
    [AiCallsAmbientSound.MountainOutdoor]: 'Mountain outdoor',
    [AiCallsAmbientSound.StaticNoise]: 'Static noise',
}

export const AMBIENT_SOUND_ORDER: AiCallsAmbientSound[] = [
    AiCallsAmbientSound.Off,
    AiCallsAmbientSound.CallCenter,
    AiCallsAmbientSound.CoffeeShop,
    AiCallsAmbientSound.ConventionHall,
    AiCallsAmbientSound.SummerOutdoor,
    AiCallsAmbientSound.MountainOutdoor,
    AiCallsAmbientSound.StaticNoise,
]

// ----------------------------------------------------------------------------
// Agent type (chosen once, at initialization)
// ----------------------------------------------------------------------------

/** Agent type used when nobody picks one. Matches the server default. */
export const DEFAULT_AI_CALLS_AGENT_TYPE: AiCallsAgentType =
    AiCallsAgentType.ConversationFlow

/** Dropdown order for the agent type picker. */
export const AI_CALLS_AGENT_TYPE_ORDER: AiCallsAgentType[] = [
    AiCallsAgentType.ConversationFlow,
    AiCallsAgentType.SinglePrompt,
]

export const AI_CALLS_AGENT_TYPE_LABELS: Record<AiCallsAgentType, string> = {
    [AiCallsAgentType.ConversationFlow]: 'Conversation Flow (standard)',
    [AiCallsAgentType.SinglePrompt]: 'Single Prompt',
}

export const AI_CALLS_AGENT_TYPE_DESCRIPTIONS: Record<
    AiCallsAgentType,
    string
> = {
    [AiCallsAgentType.ConversationFlow]:
        'Runs on the shared Chckvet call flow. Improvements roll out to this clinic automatically with every flow deploy.',
    [AiCallsAgentType.SinglePrompt]:
        'Runs on one prompt with every tool available at once. The clinic gets its own copy that can be hand-tuned in the Retell dashboard, so flow deploys do not update it.',
}

export function isAiCallsAgentType(value: string): value is AiCallsAgentType {
    return Object.values(AiCallsAgentType).some(
        agentType => agentType === value,
    )
}

// ----------------------------------------------------------------------------
// Default preset ids
//
// There is no separate "Platform default" entry in the dropdowns — they
// just list the real options. When a clinic hasn't overridden a setting
// (stored value is null), the select displays the default option as
// selected. These constants resolve which option id that is. The stored
// value stays null/inherit until the admin actively picks something, so
// future server-side default changes still propagate to untouched clinics.
// ----------------------------------------------------------------------------

/** Preset id matching the platform-default voice speed. */
export const PLATFORM_DEFAULT_VOICE_SPEED_PRESET_ID =
    VOICE_SPEED_PRESETS.find(p => p.value === PLATFORM_DEFAULT_VOICE_SPEED)
        ?.id ?? VOICE_SPEED_PRESETS[0].id

/** Preset id matching the platform-default interruption sensitivity. */
export const PLATFORM_DEFAULT_INTERRUPTION_SENSITIVITY_PRESET_ID =
    INTERRUPTION_SENSITIVITY_PRESETS.find(
        p => p.value === PLATFORM_DEFAULT_INTERRUPTION_SENSITIVITY,
    )?.id ?? INTERRUPTION_SENSITIVITY_PRESETS[0].id

/**
 * Friendly names for the merge tags Chckvet fills on every call (the server
 * sends the tag list). Tags missing here fall back to the raw tag name.
 */
export const AI_AGENT_MERGE_TAG_LABELS: Readonly<Record<string, string>> = {
    clinic_name: 'Clinic name',
    clinic_transfer_number: 'Clinic phone',
    clinic_address_display: 'Address',
    clinic_hours_display: 'Hours',
    clinic_open_now_display: 'Open or closed right now',
    supported_species_display: 'Species you see',
    bookable_services_display: 'Bookable services',
    clinic_doctors_display: 'Doctors',
    requires_new_client_address: 'New clients need an address',
    requires_new_client_birthday: 'New clients need a birthday',
    allows_new_clients: 'Taking new clients',
    ai_agent_style: 'Agent Style setting',
    ai_agent_custom_instructions: 'Custom Instructions setting',
    nearby_clinics_display: 'Other locations',
    current_date_display: "Today's date and time",
}
